/**
 * Copyright (c) 2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { __assign, __awaiter, __generator } from "tslib";
import { clamp } from '../../../mol-math/interpolate';
import { Quat } from '../../../mol-math/linear-algebra/3d/quat';
import { Vec3 } from '../../../mol-math/linear-algebra/3d/vec3';
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { PluginStateAnimation } from '../model';

var _dir = Vec3(),
    _axis = Vec3(),
    _rot = Quat();

export var AnimateCameraSpin = PluginStateAnimation.create({
  name: 'built-in.animate-camera-spin',
  display: {
    name: 'Camera Spin'
  },
  isExportable: true,
  params: function () {
    return {
      durationInMs: PD.Numeric(4000, {
        min: 100,
        max: 20000,
        step: 100
      }),
      speed: PD.Numeric(1, {
        min: 1,
        max: 10,
        step: 1
      }, {
        description: 'How many times to spin in the specified dutation.'
      }),
      direction: PD.Select('cw', [['cw', 'Clockwise'], ['ccw', 'Counter Clockwise']], {
        cycle: true
      })
    };
  },
  initialState: function (_, ctx) {
    var _a;

    return {
      snapshot: (_a = ctx.canvas3d) === null || _a === void 0 ? void 0 : _a.camera.getSnapshot()
    };
  },
  getDuration: function (p) {
    return {
      kind: 'fixed',
      durationMs: p.durationInMs
    };
  },
  teardown: function (_, state, ctx) {
    var _a;

    (_a = ctx.canvas3d) === null || _a === void 0 ? void 0 : _a.requestCameraReset({
      snapshot: state.snapshot,
      durationMs: 0
    });
  },
  apply: function (animState, t, ctx) {
    var _a, _b;

    return __awaiter(this, void 0, void 0, function () {
      var snapshot, phase, angle, position;
      return __generator(this, function (_c) {
        if (t.current === 0) {
          return [2
          /*return*/
          , {
            kind: 'next',
            state: animState
          }];
        }

        snapshot = animState.snapshot;

        if (snapshot.radiusMax < 0.0001) {
          return [2
          /*return*/
          , {
            kind: 'finished'
          }];
        }

        phase = clamp(t.current / ctx.params.durationInMs, 0, 1);

        if (phase >= 0.99999) {
          (_a = ctx.plugin.canvas3d) === null || _a === void 0 ? void 0 : _a.requestCameraReset({
            snapshot: snapshot,
            durationMs: 0
          });
          return [2
          /*return*/
          , {
            kind: 'finished'
          }];
        }

        angle = 2 * Math.PI * phase * ctx.params.speed * (ctx.params.direction === 'ccw' ? -1 : 1);
        Vec3.sub(_dir, snapshot.position, snapshot.target);
        Vec3.normalize(_axis, snapshot.up);
        Quat.setAxisAngle(_rot, _axis, angle);
        Vec3.transformQuat(_dir, _dir, _rot);
        position = Vec3.add(Vec3(), snapshot.target, _dir);
        (_b = ctx.plugin.canvas3d) === null || _b === void 0 ? void 0 : _b.requestCameraReset({
          snapshot: __assign(__assign({}, snapshot), {
            position: position
          }),
          durationMs: 0
        });
        return [2
        /*return*/
        , {
          kind: 'next',
          state: animState
        }];
      });
    });
  }
});